import React, { useCallback } from 'react';
import classNames from 'classnames';

// --------------------------------------------------------

import Modal from '@components/modal';
import DynamicForm from '@components/form';
import Button from '@components/button';
import Image from '@components/image';
import Content from '@components/content';

// --------------------------------------------------------

import { useModalAccessibility } from '@lib/utils/modalUtils';
import { RichTextField } from '@lib/interfaces';
import { ModalContentType } from '../../types/modal';

// --------------------------------------------------------

import styles from './styles.module.scss';

const {
  content_modal_wrapper,
  content_modal_header,
  content_modal_title,
  content_modal_description,
  content_modal_image,
  content_modal_content,
  content_modal_ctas,
  content_modal_form_container
} = styles;

// --------------------------------------------------------

export interface ContentModalProps {
  modalData: ModalContentType;
  isOpen: boolean;
  onClose: () => void;
  triggerElement?: HTMLElement | null;
  pageUrl?: string;
  contextData?: Record<string, unknown>;
  theme?: string;
}

/**
 * ContentModal Component
 *
 * A flexible CMS-powered modal component that can display either:
 * - Rich text/image content with optional CTAs
 * - Dynamic forms integrated with the existing form system
 *
 * Features:
 * - Full accessibility support (ARIA, focus trap, keyboard navigation)
 * - Form integration with contextual metadata
 * - Mobile responsive design
 * - Multiple theme support
 */
const ContentModal: React.FC<ContentModalProps> = ({
  modalData,
  isOpen,
  onClose,
  triggerElement,
  pageUrl,
  contextData,
  theme
}) => {
  if (!modalData) {
    return null;
  }

  const { modalRef, ids, ariaAttributes } = useModalAccessibility(
    modalData.sys.id,
    isOpen,
    onClose,
    triggerElement
  );

  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderModalContent = () => {
    if (modalData.modalType === 'form' && modalData.form) {
      return (
        <div className={content_modal_form_container}>
          <DynamicForm
            {...modalData.form}
            extraRecipients={modalData.form.extraRecipients || []}
            emailSubject={
              modalData.form.emailSubject || `Form submission from ${modalData.internalTitle}`
            }
            theme="modal"
            contextData={{
              pageUrl,
              modalId: modalData.sys?.id,
              ctaLabel: contextData?.ctaLabel,
              ...contextData
            }}
            supportHeader={null}
            supportImage={null}
            supportDescription={null}
            backgroundImage={null}
          />
        </div>
      );
    }

    return (
      <div className={content_modal_content}>
        {modalData.image?.image && (
          <div className={content_modal_image}>
            <Image
              image={{
                ...modalData.image.image,
                title: modalData.image.image.alt
              }}
              layout="responsive"
              objectFit="cover"
            />
          </div>
        )}

        {modalData.heading && (
          <div className={content_modal_header}>
            <h2 id={ids.titleId} className={content_modal_title}>
              {modalData.heading}
            </h2>
          </div>
        )}

        {modalData.description && (
          <div id={ids.descriptionId} className={content_modal_description}>
            {typeof modalData.description === 'string' ? (
              <p>{modalData.description}</p>
            ) : (
              <Content content={modalData.description as RichTextField} />
            )}
          </div>
        )}

        {modalData.ctasCollection?.items && modalData.ctasCollection.items.length > 0 && (
          <div className={content_modal_ctas}>
            {modalData.ctasCollection.items.map((cta, index) => (
              <Button
                key={index}
                theme={cta.theme}
                label={cta.label}
                url={cta.url}
                onClick={cta.url ? undefined : handleModalClose}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const modalClasses = classNames(content_modal_wrapper, {
    [`theme-${theme}`]: theme
  });

  const modalTheme = modalData.modalType === 'form' ? 'form' : 'cta-text-modal';

  return (
    <Modal
      modalIsOpen={isOpen}
      onModalClose={handleModalClose}
      theme={modalTheme}
      contentLabel={modalData.heading || modalData.internalTitle}
      content={
        <div ref={modalRef} className={modalClasses} {...ariaAttributes}>
          <div id={ids.contentId}>{renderModalContent()}</div>
        </div>
      }
    />
  );
};

export default ContentModal;
